import {
  AllTypeService,
  BookingServiceItem,
  DiscountAppliedUnique,
  KeysPointsUsed,
  KeysUsed,
  PromotionTable,
  PromotionType,
  Service,
} from "@okeys/shared-utils"

// Adapter le type AppliedDiscount aux enums réels
export type AppliedDiscount = {
  type:
    | "promotion_percentage"
    | "promotion_fixed_amount"
    | "promotion_buy_x_get_y_discount"
  promotionId: string
  discountAmount: number
}

// Fonction principale
export const applyPromotion = (
  promotions: PromotionTable<PromotionType>[],
  services: BookingServiceItem[],
  keysUsed: KeysUsed[] = [],
  availableServices: Service<AllTypeService>[] = [],
  clientId?: string
): BookingServiceItem[] => {
  // Créer une copie des services pour ne pas muter l'original
  let updatedServices = services.map((service) => ({
    ...service,
    discountApplied: null as DiscountAppliedUnique<"booking"> | null,
  }))

  // ÉTAPE 1: Appliquer d'abord les keys_points
  updatedServices = applyKeysPoints(
    updatedServices,
    keysUsed,
    availableServices
  )

  // ÉTAPE 2: Trier les promotions par priorité (prix fixe > pourcentage > buy x get y)
  const sortedPromotions = [...promotions].sort((a, b) => {
    const priorityOrder = {
      fixedPrice: 1,
      percentageDiscount: 2,
      buyXGetYDiscount: 3,
    }
    return priorityOrder[a.type] - priorityOrder[b.type]
  })

  // ÉTAPE 3: Appliquer chaque promotion une par une
  for (const promotion of sortedPromotions) {
    if (!isPromotionApplicable(promotion, updatedServices, clientId)) continue
    updatedServices = applySinglePromotion(promotion, updatedServices)
  }

  return updatedServices
}

// Nouvelle fonction pour appliquer les keys points
const applyKeysPoints = (
  services: BookingServiceItem[],
  keysUsed: KeysUsed[],
  availableServices: Service<AllTypeService>[]
): BookingServiceItem[] => {
  const updatedServices = [...services]
  const now = new Date()

  keysUsed.forEach(({ serviceId, numberItemWithKeysUsed }) => {
    const serviceInfo = availableServices.find((s) => s.id === serviceId)
    if (!serviceInfo || serviceInfo.fidelityExclusion) return

    const eligibleServices = updatedServices.filter(
      (service) =>
        service.serviceId === serviceId &&
        !service.discountApplied &&
        !service.cancelled
    )

    const servicesToDiscount = eligibleServices.slice(0, numberItemWithKeysUsed)

    servicesToDiscount.forEach((service) => {
      const keysRequired = serviceInfo.fidelityPoints || 1
      const discountPercentage = serviceInfo.fidelityPercentageDiscount || 0
      const discountAmount = service.price * (discountPercentage / 100)
      const newPrice = Math.max(0, service.price - discountAmount)

      const keysPointsDiscount: KeysPointsUsed<"booking"> = {
        type: "keys_points",
        keysUsed: keysRequired,
        priceEffect: {
          oldPrice: service.price,
          newPrice: newPrice,
        },
        timestamp: now.getTime(),
      }

      service.discountApplied = keysPointsDiscount
    })
  })

  return updatedServices
}

// Vérifie si une promotion est applicable
const isPromotionApplicable = (
  promotion: PromotionTable<PromotionType>,
  services: BookingServiceItem[],
  clientId?: string
): boolean => {
  if (!promotion.isActive) return false
  if (promotion.startDate && new Date(promotion.startDate) > new Date())
    return false
  if (promotion.endDate && new Date(promotion.endDate) < new Date())
    return false
  if (
    promotion.eligibleClientIds?.length &&
    clientId &&
    !promotion.eligibleClientIds.includes(clientId)
  )
    return false

  switch (promotion.type) {
    case "buyXGetYDiscount":
      return checkBuyXGetYDiscountApplicable(
        promotion as PromotionTable<"buyXGetYDiscount">,
        services
      )
    case "fixedPrice":
      return checkFixedPriceApplicable(
        promotion as PromotionTable<"fixedPrice">,
        services
      )
    case "percentageDiscount":
      return checkPercentageDiscountApplicable(
        promotion as PromotionTable<"percentageDiscount">,
        services
      )
    default:
      return false
  }
}

// Implémentation des checks améliorée
const checkBuyXGetYDiscountApplicable = (
  promotion: PromotionTable<"buyXGetYDiscount">,
  services: BookingServiceItem[]
): boolean => {
  const { buyQuantity, buyProductIds, getQuantity, getProductIds } =
    promotion.data

  const eligibleBuyServices = services.filter(
    (s) =>
      buyProductIds.includes(s.serviceId) && !s.discountApplied && !s.cancelled
  )

  const eligibleGetServices = services.filter(
    (s) =>
      getProductIds.includes(s.serviceId) && !s.discountApplied && !s.cancelled
  )

  return (
    eligibleBuyServices.length >= buyQuantity &&
    eligibleGetServices.length >= getQuantity
  )
}

const checkFixedPriceApplicable = (
  promotion: PromotionTable<"fixedPrice">,
  services: BookingServiceItem[]
): boolean => {
  const { productIds } = promotion.data
  return services.some(
    (s) =>
      productIds.includes(s.serviceId) && !s.discountApplied && !s.cancelled
  )
}

const checkPercentageDiscountApplicable = (
  promotion: PromotionTable<"percentageDiscount">,
  services: BookingServiceItem[]
): boolean => {
  const { productIds } = promotion.data
  return services.some(
    (s) =>
      productIds.includes(s.serviceId) && !s.discountApplied && !s.cancelled
  )
}

// Applique une promotion aux services - Version améliorée
const applySinglePromotion = (
  promotion: PromotionTable<PromotionType>,
  services: BookingServiceItem[]
): BookingServiceItem[] => {
  const updated = [...services]
  const now = new Date()

  switch (promotion.type) {
    case "buyXGetYDiscount": {
      const typedPromotion = promotion as PromotionTable<"buyXGetYDiscount">
      const { buyQuantity, buyProductIds, getQuantity, getProductIds } =
        typedPromotion.data

      const eligibleBuyServices = []
      const eligibleGetServices = []
      const eligibleBuyAndGetServices = []

      for (const service of updated) {
        if (
          buyProductIds.includes(service.serviceId) &&
          getProductIds.includes(service.serviceId) &&
          !service.discountApplied &&
          !service.cancelled
        ) {
          eligibleBuyAndGetServices.push(service)
        } else if (
          buyProductIds.includes(service.serviceId) &&
          !service.discountApplied &&
          !service.cancelled
        ) {
          eligibleBuyServices.push(service)
        } else if (
          getProductIds.includes(service.serviceId) &&
          !service.discountApplied &&
          !service.cancelled
        ) {
          eligibleGetServices.push(service)
        }
      }
      eligibleBuyAndGetServices.sort((a, b) => a.price - b.price)
      eligibleBuyServices.sort((a, b) => a.price - b.price)
      eligibleGetServices.sort((a, b) => a.price - b.price)

      const allEligibleServicesCount =
        eligibleBuyAndGetServices.length +
        eligibleBuyServices.length +
        eligibleGetServices.length

      const countPromotionCanBeApplied = Math.floor(
        allEligibleServicesCount / (buyQuantity + getQuantity)
      )

      const groups: {
        buyServices: BookingServiceItem[]
        getServices: BookingServiceItem[]
      }[] = []

      for (let i = 0; i < countPromotionCanBeApplied; i++) {
        const group: {
          buyServices: BookingServiceItem[]
          getServices: BookingServiceItem[]
        } = {
          buyServices: [],
          getServices: [],
        }

        const buyServiceList = [
          ...eligibleBuyAndGetServices,
          ...eligibleBuyServices,
        ].sort((a, b) => b.price - a.price)

        for (const service of buyServiceList) {
          if (group.buyServices.length < buyQuantity) {
            group.buyServices.push(service)
            const indexEligibleBuyAndGetServices =
              eligibleBuyAndGetServices.findIndex(
                (s) => s.serviceId === service.serviceId
              )
            if (indexEligibleBuyAndGetServices !== -1) {
              eligibleBuyAndGetServices.splice(
                indexEligibleBuyAndGetServices,
                1
              )
            }
            const indexEligibleBuyServices = eligibleBuyServices.findIndex(
              (s) => s.serviceId === service.serviceId
            )
            if (indexEligibleBuyServices !== -1) {
              eligibleBuyServices.splice(indexEligibleBuyServices, 1)
            }
          } else {
            break
          }
        }

        const getServiceList = [
          ...eligibleBuyAndGetServices,
          ...eligibleGetServices,
        ].sort((a, b) => a.price - b.price)

        for (const service of getServiceList) {
          if (group.getServices.length < getQuantity) {
            group.getServices.push(service)
            const indexEligibleBuyAndGetServices =
              eligibleBuyAndGetServices.findIndex(
                (s) => s.serviceId === service.serviceId
              )
            if (indexEligibleBuyAndGetServices !== -1) {
              eligibleBuyAndGetServices.splice(
                indexEligibleBuyAndGetServices,
                1
              )
            }
            const indexEligibleGetServices = eligibleGetServices.findIndex(
              (s) => s.serviceId === service.serviceId
            )
            if (indexEligibleGetServices !== -1) {
              eligibleGetServices.splice(indexEligibleGetServices, 1)
            }
          } else {
            break
          }
        }

        groups.push(group)
      }

      const groupsFiltered = groups.filter(
        (group) =>
          group.buyServices.length === buyQuantity &&
          group.getServices.length > 0 &&
          group.getServices.length >= getQuantity
      )

      for (const group of groupsFiltered) {
        for (const buyService of group.buyServices) {
          buyService.discountApplied = {
            promotionCount: 1,
            isPartOfBuyGroup: true,
            isPartOfGetGroup: false,
            type: "promotion_buy_x_get_y_discount",
            promotionId: promotion.id,
            priceEffect: {
              oldPrice: buyService.price,
              newPrice: buyService.price,
            },
            timestamp: now.getTime(),
          }
        }

        for (const getService of group.getServices) {
          getService.discountApplied = {
            promotionCount: 1,
            isPartOfBuyGroup: false,
            isPartOfGetGroup: true,
            type: "promotion_buy_x_get_y_discount",
            promotionId: promotion.id,
            priceEffect: {
              oldPrice: getService.price,
              newPrice: 0,
            },
            timestamp: now.getTime(),
          }
        }
      }
      break
    }

    case "fixedPrice": {
      const typedPromotion = promotion as PromotionTable<"fixedPrice">
      const { newPrice, productIds } = typedPromotion.data

      updated.forEach((service) => {
        if (
          productIds.includes(service.serviceId) &&
          !service.discountApplied &&
          !service.cancelled &&
          service.price > newPrice // Appliquer seulement si c'est avantageux
        ) {
          service.discountApplied = {
            type: "promotion_fixed_amount",
            promotionId: promotion.id,
            priceEffect: {
              oldPrice: service.price,
              newPrice: Math.max(0, newPrice),
            },
            timestamp: now.getTime(),
          }
        }
      })
      break
    }

    case "percentageDiscount": {
      const typedPromotion = promotion as PromotionTable<"percentageDiscount">
      const { discountPercentage, productIds } = typedPromotion.data

      updated.forEach((service) => {
        if (
          productIds.includes(service.serviceId) &&
          !service.discountApplied &&
          !service.cancelled
        ) {
          const discountAmount = service.price * (discountPercentage / 100)
          const newPrice = Math.max(0, service.price - discountAmount)

          service.discountApplied = {
            type: "promotion_percentage",
            promotionId: promotion.id,
            priceEffect: {
              oldPrice: service.price,
              newPrice: newPrice,
            },
            timestamp: now.getTime(),
          }
        }
      })
      break
    }
  }

  return updated
}
